import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { apiClient } from '@/lib/api'
import { useT } from '@/i18n/I18nProvider'

const Schema = z.object({
  domain: z.string().min(3).regex(/^[a-z0-9.-]+\.[a-z]{2,}$/i, 'Invalid domain'),
  baseUrl: z.string().url(),
  businessType: z.enum(['local', 'ecommerce', 'saas', 'media', 'other']),
})
type FormValues = z.infer<typeof Schema>

interface SiteResponse {
  id: string
  domain: string
  verifyToken: string
}

export function OnboardPage() {
  const t = useT()
  const navigate = useNavigate()
  const [created, setCreated] = useState<SiteResponse | null>(null)

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(Schema),
    defaultValues: { businessType: 'other' },
  })

  const create = useMutation({
    mutationFn: (values: FormValues) => apiClient.post<SiteResponse>('/sites', values),
    onSuccess: (data) => setCreated(data),
  })

  const verifyDns = useMutation({
    mutationFn: () => apiClient.post(`/sites/${created!.id}/verify-ownership`, { method: 'dns-txt' }),
    onSuccess: () => navigate(`/sites/${created!.id}`),
  })

  if (created) {
    return (
      <div className="max-w-md">
        <h1 className="text-2xl font-semibold mb-2">{t('onboard.verify')}</h1>
        <p className="text-sm opacity-70 mb-4">
          Add this TXT record at <code>_discover-verify.{created.domain}</code>:
        </p>
        <pre className="p-3 text-xs rounded" style={{ background: 'var(--color-border)' }}>
          discover-verify={created.verifyToken}
        </pre>
        <button
          type="button"
          onClick={() => verifyDns.mutate()}
          disabled={verifyDns.isPending}
          className="mt-4 px-3 py-1.5 text-sm rounded font-medium"
          style={{ background: 'var(--color-accent)', color: 'white' }}
        >
          {t('onboard.continue')}
        </button>
      </div>
    )
  }

  return (
    <div className="max-w-md">
      <h1 className="text-2xl font-semibold mb-4">{t('onboard.title')}</h1>
      <form onSubmit={handleSubmit((v) => create.mutate(v))} className="flex flex-col gap-3">
        <label className="block">
          <span className="text-sm opacity-80">{t('onboard.domain')}</span>
          <input
            {...register('domain')}
            className="mt-1 w-full px-3 py-2 rounded border bg-transparent"
            style={{ borderColor: 'var(--color-border)' }}
            placeholder="example.com"
          />
          {errors.domain && <p className="text-xs mt-1" style={{ color: 'var(--color-critical)' }}>{errors.domain.message}</p>}
        </label>
        <label className="block">
          <span className="text-sm opacity-80">{t('onboard.baseurl')}</span>
          <input
            {...register('baseUrl')}
            className="mt-1 w-full px-3 py-2 rounded border bg-transparent"
            style={{ borderColor: 'var(--color-border)' }}
            placeholder="https://example.com"
          />
          {errors.baseUrl && <p className="text-xs mt-1" style={{ color: 'var(--color-critical)' }}>{errors.baseUrl.message}</p>}
        </label>
        <label className="block">
          <span className="text-sm opacity-80">{t('onboard.business')}</span>
          <select {...register('businessType')} className="mt-1 w-full px-3 py-2 rounded border bg-transparent" style={{ borderColor: 'var(--color-border)' }}>
            <option value="local">{t('onboard.business.local')}</option>
            <option value="ecommerce">{t('onboard.business.ecommerce')}</option>
            <option value="saas">{t('onboard.business.saas')}</option>
            <option value="media">{t('onboard.business.media')}</option>
            <option value="other">{t('onboard.business.other')}</option>
          </select>
        </label>
        <button
          type="submit"
          disabled={create.isPending}
          className="mt-2 px-3 py-2 rounded font-medium text-sm"
          style={{ background: 'var(--color-accent)', color: 'white' }}
        >
          {t('onboard.continue')}
        </button>
        {create.isError && <p className="text-xs" style={{ color: 'var(--color-critical)' }}>{(create.error as Error).message}</p>}
      </form>
    </div>
  )
}
